import { Component, OnDestroy, OnInit } from '@angular/core';
import { Log } from "ng2-logger";
import { ToolRequest } from "@models/toolRequests.model";
import { TableConfig } from "@app/shared/util/table-config.utils";
import { ToolRequestsService } from "@services/tool-requests.service";
import { ToastsManager } from "@app/shared/util/toastr.service";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { NotificationService } from "@services/notification.service";
import { ToolRequestStatus } from "@app/tool/model/enums/tool-request-status.enum";
import { Section } from "@models/enums/section.utils";
import { AppUtils } from "@app/shared/util/app.utils";

@Component({
    selector: 'app-tool-requests-facilities',
    templateUrl: './tool-requests-facilities.component.html',
    styleUrls: ['./tool-requests-facilities.component.scss']
})
export class ToolRequestsFacilitiesComponent implements OnInit, OnDestroy {

    basePath = 'tool-requests-facilities';
    _logger = Log.create(ToolRequestsFacilitiesComponent.name);

    pendingToolRequests: ToolRequest[] = [];
    tableConfig = new TableConfig();
    isLoading = false;

    newToolRequestsSubscription: Subscription;
    selectedPlantsChangedSubscription: Subscription;

    filterStatusList = [];

    constructor(private toolRequestsService: ToolRequestsService,
                private toastr: ToastsManager,
                private router: Router,
                private notificationService: NotificationService,
                private toolRequestService: ToolRequestsService,
                private translateService: TranslateService) {
        this.filterStatusList = this.buildStatusFilterColumnKeys();
        this.translateService.onLangChange.subscribe((event) => {
            this.filterStatusList = this.buildStatusFilterColumnKeys();
        });

        this.selectedPlantsChangedSubscription = this.notificationService.selectedPlantsChangedAsObservable.subscribe(data => {
            this.getPendingToolRequests(this.tableConfig);
        });
    }

    ngOnInit() {
        this.getPendingToolRequests(this.tableConfig);
        this.newToolRequestsSubscription = this.notificationService.facilitiesToolRequestsQueueObservable
            .subscribe((value) => {
                this._logger.info("ngOnInit:: facilitiesToolRequestsQueue got value", value);
                this.getPendingToolRequests(this.tableConfig);
            });

    }

    ngOnDestroy(): void {
        if (this.newToolRequestsSubscription) {
            this.newToolRequestsSubscription.unsubscribe();
        }

        if (this.selectedPlantsChangedSubscription) {
            this.selectedPlantsChangedSubscription.unsubscribe();
        }
    }

    getPendingToolRequests(tableConfig = this.tableConfig) {
        this._logger.info('getPendingToolRequests:: Retrieving pending tool requests from facilities ');
        this.isLoading = true;
        tableConfig = AppUtils.appendPlantsQuery(tableConfig);
        this.toolRequestsService.getAllToolRequests(tableConfig, 'TICKET', Section.ApproveToolRequests).subscribe(
            (data) => {
                this._logger.info('getPendingToolRequests:: API Returned data', data);
                this.pendingToolRequests = data.items;
                this.tableConfig.pager.count = data.total;
                this.isLoading = false;
            },
            (error) => {
                this._logger.error('getPendingToolRequests:: API Returned error', error);
                this.isLoading = false;
            }
        )
    }

    onSortChange(tableConfig: TableConfig) {
        this._logger.info('onSortChange:: Sorting table for config ', tableConfig);
        this.getPendingToolRequests(tableConfig);
    }

    onFilter(tableConfig: TableConfig) {
        this._logger.info('onFilter:: Filtering table for config ', tableConfig);
        this.getPendingToolRequests(tableConfig);
    }

    onPageChange(tableConfig: TableConfig) {
        this._logger.info('onPageChange:: Filtering table for config ', tableConfig);
        this.getPendingToolRequests(tableConfig);
    }

    onProcess(toolRequest: ToolRequest) {
        this._logger.info('onProcess:: Opening process page for toolRequest ', toolRequest);
        this.startApproval(toolRequest.id, toolRequest.name);
    }

    onResume(toolRequest: ToolRequest) {
        this._logger.info('onResume:: Opening process page for toolRequest ', toolRequest);
        this.router.navigate([`${this.basePath}/${toolRequest.id}`]);
    }

    startApproval(id: number, toolRequestName: string = '') {
        this._logger.info("startApproval:: Starting approval process for tool request id", id);
        this.toolRequestService.startApprovalById(id).subscribe(
            (data) => {
                this._logger.info('startApproval:: Tool Request processing started', data);
                this.router.navigate([`${this.basePath}/${id}`]);
            },
            (error) => {
                this._logger.error('startApproval:: API Returned error', error);
                this.toastr.error(this.buildCannotStartProcessErrorMessage(toolRequestName));
                this.router.navigate([`${this.basePath}/${id}`]);
            }
        );
    }

    buildCannotStartProcessErrorMessage(toolRequestName) {
        return this.translateService.instant('atom.tool-requests-facilities.toasts.cannotStartProcess',
            {toolRequestName: toolRequestName});
    }

    buildStatusFilterColumnKeys() {
        return [
            {name: this.translateService.instant("generic.statuses.all"), value: 'All'},
            {name: this.translateService.instant("generic.statuses.new"), value: ToolRequestStatus.NEW},
            {name: this.translateService.instant("generic.statuses.waitingForApproval"), value: ToolRequestStatus.WAITING_FOR_APPROVAL},
        ];
    }
}
